// Renders a card for one of the 5 backend copilot recommendation tools
// (F1 recommendIrrigationDose, F2 analyzeIrrigationEfficiency,
// F3 detectFlowAnomalies, F4 recommendWeatherAdjustment,
// F5 analyzeMoistureTrend). All 5 tools emit RECOMMEND shapes (they do NOT
// auto-execute). F2 and F4 surface a non-null `proposedUpdate` that the
// existing human-in-the-loop confirm widget (ProposedActionCard via
// CopilotPanel.handleConfirmAction) is responsible for confirming — this
// component MUST NOT call updateProgram directly.
//
// Contracts are pinned verbatim in
// .local/tasks/drafts/consumed/draft-copilot-recommendation-fe.md.
// Do not invent fields; render only what the contract defines.

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import ActionArgsTable from '@/features/copilot/ActionArgsTable'

// ─── Tool name constants ──────────────────────────────────────────────
export const RECOMMENDATION_TOOL_F1 = 'recommendIrrigationDose'
export const RECOMMENDATION_TOOL_F2 = 'analyzeIrrigationEfficiency'
export const RECOMMENDATION_TOOL_F3 = 'detectFlowAnomalies'
export const RECOMMENDATION_TOOL_F4 = 'recommendWeatherAdjustment'
export const RECOMMENDATION_TOOL_F5 = 'analyzeMoistureTrend'

export const RECOMMENDATION_TOOL_NAMES: ReadonlySet<string> = new Set([
  RECOMMENDATION_TOOL_F1,
  RECOMMENDATION_TOOL_F2,
  RECOMMENDATION_TOOL_F3,
  RECOMMENDATION_TOOL_F4,
  RECOMMENDATION_TOOL_F5,
])

// ─── Result shapes (verbatim from BE contract) ────────────────────────
interface F1Station {
  id: string
  description: string | null
}

interface F1Result {
  station: F1Station
  etc_mm: number
  deficit_mm: number
  recommended_runtime_min: number
  basis: Record<string, unknown>
  caveats: string[]
}

interface F2Summary {
  interfaceId: string | number
  month: number
  year: number
  periodDays: number
  programsAnalyzed: number
  overIrrigators: number
  underIrrigators: number
  totalWasteCost: number
  waterRate: { rate: number | null; rateUnit: string | null; currencyCode: string | null } | null
  et0MmPerDay: number | null
  et0Source: string | null
}

interface F2Recommendation {
  newWaterAllocationMm: number | null
  newWaterBudgetPercent: number | null
  proposedUpdate:
    | { id: number; interfaceId: number; waterAllocation: number; waterBudget: number }
    | null
}

interface F2Program {
  program: Record<string, unknown>
  actualWaterL: number
  etcNeedL: number
  overUnderPct: number
  deltaL: number
  wasteCost: number | null
  status: 'over' | 'under' | 'in_tolerance' | 'unknown'
  recommendation: F2Recommendation
  basis: Record<string, unknown>
}

interface F2Result {
  summary: F2Summary
  programs: F2Program[]
  caveats: string[]
}

interface F2Error {
  error: 'no_programs'
  message: string
  interfaceId: number
  month: number
  year: number
  periodDays: number
  caveats?: string[]
}

interface F3SeverityBuckets {
  high: number
  medium: number
  low: number
}

interface F3Summary {
  flowLogRecords: number
  stationsScanned: number
  valveErrors: number
  twoWireDecodersScanned: number
  findingsCount: number
  bySeverity: F3SeverityBuckets
  valveErrorsWarning?: string
  twoWireWarning?: string
}

interface F3Finding {
  station_or_valve: string
  source: 'station' | 'valve' | 'two_wire_decoder'
  signature: string
  severity: 'high' | 'medium' | 'low'
  score: number
  evidence: Array<{ metric: string; value: number | string; detail?: string }>
  suggested_action: string
}

interface F3Result {
  interfaceId: number
  windowDays: number
  summary: F3Summary
  findings: F3Finding[]
}

interface F3Error {
  error: 'flow_log_fetch_failed'
  message: string
  interfaceId: number
  windowDays: number
}

interface F4Program {
  id: string
  name: string | null
  mode: string | null
  currentWaterBudget: number
}

interface F4LookAhead {
  hours: number
  effectiveRainMm: number
  etcNeedMm: number
  coverageRatio: number
}

interface F4PerStation {
  stationId: string
  stationDescription: string | null
  etc_mm_per_day: number
  caveats: string[]
  error?: string
}

interface F4Forecast {
  slotsUsed: number
  totalRainMm: number
  maxPop: number | null
}

interface F4Result {
  program: F4Program
  lookAhead: F4LookAhead
  action: 'keep' | 'reduce' | 'skip_next_cycle'
  recommendedWaterBudget: number
  waterBudgetDelta: number
  perStation: F4PerStation[]
  forecast: F4Forecast
  proposedUpdate: { id: number; waterBudget: number; applyVia: 'updateProgram' } | null
  caveats: string[]
}

interface F5Projection {
  days: number
  projectedMoisturePercent: number
}

interface F5Metrics {
  sampleCount: number
  meanMoisturePercent: number
  stdDevMoisturePercent: number
  lastMoisturePercent: number
  windowFromDate: string
  windowEndDate: string
  runCountInWindow: number
}

interface F5Result {
  classification: 'stable' | 'drying' | 'recovering' | 'chronic_dry'
  slope: number
  projection: F5Projection
  likely_cause: 'emitter' | 'over-ET' | 'root_change' | null
  recommendation: string
  metrics: F5Metrics
  programHistoryWarning?: string
}

interface F5ErrorHistory {
  error: 'history_fetch_failed'
  message: string
  interfaceId: number
  sensorId: string
  windowFromDate: string
  windowEndDate: string
}

interface F5ErrorInsufficient {
  error: 'insufficient_data'
  message: string
  interfaceId: number
  sensorId: string
  windowFromDate: string
  windowEndDate: string
  sampleCount: number
}

type F5Error = F5ErrorHistory | F5ErrorInsufficient

// ─── Type guards (defensive — never throw) ────────────────────────────
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

const isF1Result = (value: unknown): value is F1Result => {
  if (!isObject(value)) return false
  if ('error' in value) return false
  const station = value.station
  const basis = value.basis
  return (
    isObject(station) &&
    typeof station.id === 'string' &&
    (station.description === null || typeof station.description === 'string') &&
    isNumber(value.etc_mm) &&
    isNumber(value.deficit_mm) &&
    isNumber(value.recommended_runtime_min) &&
    isObject(basis) &&
    isStringArray(value.caveats)
  )
}

const isF2Error = (value: unknown): value is F2Error => {
  if (!isObject(value)) return false
  return (
    value.error === 'no_programs' &&
    typeof value.message === 'string' &&
    isNumber(value.interfaceId) &&
    isNumber(value.month) &&
    isNumber(value.year) &&
    isNumber(value.periodDays)
  )
}

const isF2Result = (value: unknown): value is F2Result => {
  if (!isObject(value)) return false
  if (isF2Error(value)) return false
  if (!isObject(value.summary)) return false
  if (!Array.isArray(value.programs)) return false
  if (!isStringArray(value.caveats)) return false
  return isNumber(value.summary.programsAnalyzed)
}

const isF3Error = (value: unknown): value is F3Error => {
  if (!isObject(value)) return false
  return (
    value.error === 'flow_log_fetch_failed' &&
    typeof value.message === 'string' &&
    isNumber(value.interfaceId) &&
    isNumber(value.windowDays)
  )
}

const isF3Result = (value: unknown): value is F3Result => {
  if (!isObject(value)) return false
  if (isF3Error(value)) return false
  if (!isObject(value.summary)) return false
  if (!Array.isArray(value.findings)) return false
  return isNumber(value.summary.findingsCount)
}

const isF4Result = (value: unknown): value is F4Result => {
  if (!isObject(value)) return false
  if (!isObject(value.program)) return false
  if (!isObject(value.lookAhead)) return false
  if (!Array.isArray(value.perStation)) return false
  if (!isObject(value.forecast)) return false
  if (!isStringArray(value.caveats)) return false
  if (
    value.action !== 'keep' &&
    value.action !== 'reduce' &&
    value.action !== 'skip_next_cycle'
  ) {
    return false
  }
  if (value.proposedUpdate !== null) {
    if (!isObject(value.proposedUpdate)) return false
    const u = value.proposedUpdate
    if (
      !isNumber(u.id) ||
      !isNumber(u.waterBudget) ||
      u.applyVia !== 'updateProgram'
    ) {
      return false
    }
  }
  return (
    isNumber(value.recommendedWaterBudget) &&
    isNumber(value.waterBudgetDelta) &&
    typeof value.program.id === 'string'
  )
}

const isF5Error = (value: unknown): value is F5Error => {
  if (!isObject(value)) return false
  return value.error === 'history_fetch_failed' || value.error === 'insufficient_data'
}

const isF5Result = (value: unknown): value is F5Result => {
  if (!isObject(value)) return false
  if (isF5Error(value)) return false
  if (
    value.classification !== 'stable' &&
    value.classification !== 'drying' &&
    value.classification !== 'recovering' &&
    value.classification !== 'chronic_dry'
  ) {
    return false
  }
  if (!isObject(value.projection)) return false
  if (!isObject(value.metrics)) return false
  return typeof value.recommendation === 'string'
}

// ─── Helpers ─────────────────────────────────────────────────────────
const labelSx = {
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  color: 'grey.800',
}

const fieldLabelSx = {
  fontSize: 11.5,
  fontWeight: 500,
  color: 'grey.800',
}

const fieldValueSx = {
  fontSize: 12.5,
  color: 'text.main',
  wordBreak: 'break-word' as const,
}

const caveatSx = {
  fontSize: 12,
  color: 'text.main',
  pl: 1.25,
  position: 'relative' as const,
  '&::before': {
    content: '""',
    position: 'absolute' as const,
    left: 0,
    top: 0.55,
    width: 4,
    height: 4,
    borderRadius: '50%',
    bgcolor: 'grey.600',
  },
}

const formatNumber = (n: number, digits: number = 1): string => {
  if (!Number.isFinite(n)) return '—'
  return Number(n).toFixed(digits).replace(/\.0+$/, '')
}

const formatMoney = (n: number | null, rate: F2Summary['waterRate']): string => {
  if (n === null || !Number.isFinite(n)) return '—'
  const currency = rate?.currencyCode ?? ''
  const unit = rate?.rateUnit ?? ''
  const formatted = Math.abs(n) >= 100
    ? n.toFixed(0)
    : n.toFixed(2).replace(/\.?0+$/, '')
  if (currency && unit) return `${formatted} ${currency}/${unit}`
  if (currency) return `${formatted} ${currency}`
  return formatted
}

const SEVERITY_BG: Record<F3Finding['severity'], string> = {
  high: '#ff424a',
  medium: '#EECD1D',
  low: '#58A6FF',
}

const SEVERITY_LABEL: Record<F3Finding['severity'], string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const F4_ACTION_LABEL: Record<F4Result['action'], string> = {
  keep: 'Keep current program',
  reduce: 'Reduce water budget',
  skip_next_cycle: 'Skip next cycle',
}

const F5_CLASSIFICATION_LABEL: Record<F5Result['classification'], string> = {
  stable: 'Stable',
  drying: 'Drying',
  recovering: 'Recovering',
  chronic_dry: 'Chronically dry',
}

const F5_CAUSE_LABEL: Record<NonNullable<F5Result['likely_cause']>, string> = {
  emitter: 'Likely cause: emitter issue',
  'over-ET': 'Likely cause: high evapotranspiration',
  root_change: 'Likely cause: root-zone change',
}

const F2_STATUS_LABEL: Record<F2Program['status'], string> = {
  over: 'Over',
  under: 'Under',
  in_tolerance: 'In tolerance',
  unknown: 'Unknown',
}

const F2_STATUS_BG: Record<F2Program['status'], string> = {
  over: '#ff424a',
  under: '#EECD1D',
  in_tolerance: '#2ea043',
  unknown: '#7C7B7B',
}

// ─── Shared subcomponents ────────────────────────────────────────────
interface RationaleSectionProps {
  basis?: unknown
  caveats?: string[]
  basisLabel?: string
}

const RationaleSection = ({
  basis,
  caveats,
  basisLabel = 'Basis',
}: RationaleSectionProps) => {
  const hasBasis = basis !== undefined && basis !== null && !(
    isObject(basis) && Object.keys(basis).length === 0
  )
  const hasCaveats = Array.isArray(caveats) && caveats.length > 0
  if (!hasBasis && !hasCaveats) return null
  return (
    <Box
      component="details"
      sx={{
        mt: 0.75,
        '& > summary': {
          cursor: 'pointer',
          fontSize: 11.5,
          color: 'grey.600',
          userSelect: 'none',
          py: 0.25,
          outline: 'none',
          listStyle: 'none',
          '&::-webkit-details-marker': { display: 'none' },
          '&:hover': { color: 'text.main' },
        },
      }}
    >
      <Box component="summary">Show rationale</Box>
      <Box
        sx={{
          mt: 0.5,
          p: 1,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        {hasBasis && (
          <Box sx={{ mb: hasCaveats ? 1 : 0 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>{basisLabel}</Typography>
            <ActionArgsTable data={basis} />
          </Box>
        )}
        {hasCaveats && (
          <Box>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Caveats</Typography>
            <Stack spacing={0.4}>
              {(caveats as string[]).map((c, idx) => (
                <Typography key={idx} sx={caveatSx}>
                  {c}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface ErrorBannerProps {
  message: string
  title?: string
}

const ErrorBanner = ({ message, title = 'Not enough data' }: ErrorBannerProps) => (
  <Alert severity="warning" variant="outlined" sx={{ fontSize: 13 }}>
    <AlertTitle sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }}>
      {title}
    </AlertTitle>
    <Typography sx={{ fontSize: 12.5, color: 'text.main' }}>
      {message}
    </Typography>
  </Alert>
)

const SeverityBadge = ({ severity }: { severity: F3Finding['severity'] }) => (
  <Chip
    label={SEVERITY_LABEL[severity]}
    size="small"
    sx={{
      height: 18,
      borderRadius: 0.75,
      fontSize: 10.5,
      fontWeight: 600,
      color: '#fff',
      bgcolor: SEVERITY_BG[severity],
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

const StatusBadge = ({ status }: { status: F2Program['status'] }) => (
  <Chip
    label={F2_STATUS_LABEL[status]}
    size="small"
    sx={{
      height: 18,
      borderRadius: 0.75,
      fontSize: 10.5,
      fontWeight: 600,
      color: '#fff',
      bgcolor: F2_STATUS_BG[status],
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

const ClassificationBadge = ({
  classification,
}: {
  classification: F5Result['classification']
}) => {
  const color =
    classification === 'stable' || classification === 'recovering'
      ? '#2ea043'
      : classification === 'drying'
        ? '#EECD1D'
        : '#ff424a'
  return (
    <Chip
      label={F5_CLASSIFICATION_LABEL[classification]}
      size="small"
      sx={{
        height: 20,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        color: classification === 'drying' ? '#000' : '#fff',
        bgcolor: color,
        '& .MuiChip-label': { px: 0.85 },
      }}
    />
  )
}

const FieldRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(96px, max-content) 1fr',
      columnGap: 1.25,
      rowGap: 0.4,
      alignItems: 'baseline',
    }}
  >
    <Typography sx={fieldLabelSx}>{label}</Typography>
    <Box sx={{ minWidth: 0 }}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography component="span" sx={fieldValueSx}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  </Box>
)

// ─── Card outer shell ────────────────────────────────────────────────
const CardShell = ({
  title,
  severity,
  children,
}: {
  title: string
  severity?: 'info' | 'warning' | 'error' | 'success'
  children: React.ReactNode
}) => (
  <Alert
    severity={severity ?? 'info'}
    variant="outlined"
    sx={{ fontSize: 13, '& .MuiAlert-message': { width: '100%' } }}
  >
    <AlertTitle
      sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: 'text.main' }}
    >
      {title}
    </AlertTitle>
    {children}
  </Alert>
)

// ─── F1 recommendIrrigationDose ──────────────────────────────────────
const F1Card = ({ result }: { result: unknown }) => {
  if (!isF1Result(result)) return null
  const stationLabel = result.station.description
    ? `${result.station.description} (#${result.station.id})`
    : `#${result.station.id}`
  return (
    <CardShell title="Recommended irrigation dose" severity="info">
      <Typography
        sx={{ fontSize: 18, fontWeight: 600, color: 'text.main', lineHeight: 1.2 }}
      >
        {`${formatNumber(result.recommended_runtime_min, 1)} min`}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
        <FieldRow label="Station">{stationLabel}</FieldRow>
        <FieldRow label="ETc">{`${formatNumber(result.etc_mm, 1)} mm`}</FieldRow>
        <FieldRow label="Deficit">{`${formatNumber(result.deficit_mm, 1)} mm`}</FieldRow>
      </Stack>
      <RationaleSection basis={result.basis} caveats={result.caveats} />
    </CardShell>
  )
}

// ─── F2 analyzeIrrigationEfficiency ──────────────────────────────────
interface F2CardProps {
  result: unknown
  onApplyRecommendation?: (instruction: string) => void
  disableActions?: boolean
}

const F2Card = ({ result, onApplyRecommendation, disableActions }: F2CardProps) => {
  if (isF2Error(result)) {
    return (
      <CardShell title="Irrigation efficiency" severity="warning">
        <Typography sx={{ fontSize: 12.5, color: 'text.main' }}>
          {result.message ||
            'No irrigation programs to analyze for the selected period.'}
        </Typography>
        {result.caveats && result.caveats.length > 0 && (
          <RationaleSection caveats={result.caveats} basis={undefined} />
        )}
      </CardShell>
    )
  }
  if (!isF2Result(result)) return null
  const { summary, programs, caveats } = result
  const money = formatMoney(summary.totalWasteCost, summary.waterRate)
  return (
    <CardShell title="Irrigation efficiency" severity="info">
      <Stack spacing={0.5}>
        <FieldRow label="Programs analyzed">{summary.programsAnalyzed}</FieldRow>
        <FieldRow label="Over-irrigators">{summary.overIrrigators}</FieldRow>
        <FieldRow label="Under-irrigators">{summary.underIrrigators}</FieldRow>
        <FieldRow label="Total waste cost">{money}</FieldRow>
      </Stack>
      {programs.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>Programs</Typography>
          <Stack spacing={0.75}>
            {programs.map((prog, idx) => {
              const programName =
                (prog.program && typeof prog.program === 'object'
                  ? (prog.program as { name?: unknown }).name
                  : null) || `Program #${(prog.program as { id?: unknown })?.id ?? idx}`
              const proposed = prog.recommendation?.proposedUpdate ?? null
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'border.devider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.main' }}
                    >
                      {String(programName)}
                    </Typography>
                    <StatusBadge status={prog.status} />
                    <Typography
                      sx={{ fontSize: 12, color: 'grey.800', ml: 'auto' }}
                    >
                      {`${prog.overUnderPct > 0 ? '+' : ''}${formatNumber(prog.overUnderPct, 1)}%`}
                    </Typography>
                  </Box>
                  {prog.wasteCost !== null && Number.isFinite(prog.wasteCost) && (
                    <Typography sx={{ fontSize: 11.5, color: 'grey.800', mt: 0.25 }}>
                      {`Waste cost: ${formatMoney(prog.wasteCost, summary.waterRate)}`}
                    </Typography>
                  )}
                  {proposed && onApplyRecommendation && (
                    <Box sx={{ mt: 0.75 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={disableActions}
                        onClick={() => {
                          // proposedUpdate is a HINT, not a complete
                          // updateProgram payload. Instruct the agent to apply
                          // it; the agent re-fetches the full program and calls
                          // updateProgram, which runs the real confirm gate and
                          // surfaces a ProposedActionCard for the user.
                          onApplyRecommendation(
                            `Apply the recommended irrigation update to program #${proposed.id} on interface ${proposed.interfaceId}: set waterAllocation to ${proposed.waterAllocation} and waterBudget to ${proposed.waterBudget}%. Re-fetch the program first, then call updateProgram so I can confirm the change.`,
                          )
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Stack>
        </Box>
      )}
      <RationaleSection basis={undefined} caveats={caveats} basisLabel="Summary caveats" />
    </CardShell>
  )
}

// ─── F3 detectFlowAnomalies ──────────────────────────────────────────
const F3Card = ({ result }: { result: unknown }) => {
  if (isF3Error(result)) {
    return (
      <CardShell title="Flow / leak anomalies" severity="error">
        <ErrorBanner
          title="Could not load flow logs"
          message={
            result.message ||
            'The flow log service returned an error. Please retry later.'
          }
        />
      </CardShell>
    )
  }
  if (!isF3Result(result)) return null
  const { summary, findings } = result
  const sev = summary.bySeverity
  if (findings.length === 0) {
    return (
      <CardShell title="Flow / leak anomalies" severity="success">
        <Typography sx={{ fontSize: 12.5, color: 'text.main' }}>
          {`No anomalies detected in the last ${result.windowDays}-day window.`}
        </Typography>
      </CardShell>
    )
  }
  return (
    <CardShell
      title="Flow / leak anomalies"
      severity={sev.high > 0 ? 'error' : sev.medium > 0 ? 'warning' : 'info'}
    >
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
        {sev.high > 0 && <SeverityBadge severity="high" />}
        {sev.medium > 0 && <SeverityBadge severity="medium" />}
        {sev.low > 0 && <SeverityBadge severity="low" />}
        <Typography sx={{ fontSize: 12, color: 'grey.800', alignSelf: 'center' }}>
          {`${summary.findingsCount} finding${summary.findingsCount === 1 ? '' : 's'}`}
        </Typography>
      </Box>
      <Stack spacing={0.75}>
        {findings.map((finding, idx) => (
          <Box
            key={idx}
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'border.devider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.main' }}
              >
                {finding.station_or_valve}
              </Typography>
              <SeverityBadge severity={finding.severity} />
              <Typography sx={{ fontSize: 11.5, color: 'grey.800', ml: 'auto' }}>
                {`score ${formatNumber(finding.score, 2)}`}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 12, color: 'grey.800', mt: 0.25, fontStyle: 'italic' }}
            >
              {finding.signature}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.main', mt: 0.5 }}>
              {finding.suggested_action}
            </Typography>
            <Box
              component="details"
              sx={{
                mt: 0.5,
                '& > summary': {
                  cursor: 'pointer',
                  fontSize: 11.5,
                  color: 'grey.600',
                  userSelect: 'none',
                  listStyle: 'none',
                  '&::-webkit-details-marker': { display: 'none' },
                  '&:hover': { color: 'text.main' },
                },
              }}
            >
              <Box component="summary">Show evidence</Box>
              <Box
                sx={{
                  mt: 0.5,
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <ActionArgsTable data={finding.evidence} parentKey="evidence" />
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
      {summary.valveErrorsWarning && (
        <Typography sx={{ fontSize: 11.5, color: 'grey.800', mt: 0.5 }}>
          {summary.valveErrorsWarning}
        </Typography>
      )}
      {summary.twoWireWarning && (
        <Typography sx={{ fontSize: 11.5, color: 'grey.800', mt: 0.5 }}>
          {summary.twoWireWarning}
        </Typography>
      )}
    </CardShell>
  )
}

// ─── F4 recommendWeatherAdjustment ───────────────────────────────────
interface F4CardProps {
  result: unknown
  onApplyRecommendation?: (instruction: string) => void
  disableActions?: boolean
}

const F4Card = ({ result, onApplyRecommendation, disableActions }: F4CardProps) => {
  if (!isF4Result(result)) return null
  const { program, lookAhead, action, recommendedWaterBudget, waterBudgetDelta, perStation, forecast, proposedUpdate, caveats } = result
  const programName = program.name || `Program #${program.id}`
  const actionSeverity: 'info' | 'warning' | 'error' =
    action === 'skip_next_cycle'
      ? 'error'
      : action === 'reduce'
        ? 'warning'
        : 'info'
  const deltaText =
    waterBudgetDelta > 0
      ? `+${formatNumber(waterBudgetDelta, 1)}%`
      : `${formatNumber(waterBudgetDelta, 1)}%`
  return (
    <CardShell title="Weather adjustment" severity={actionSeverity}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          mb: 0.5,
        }}
      >
        <Typography
          sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.main' }}
        >
          {programName}
        </Typography>
        <Chip
          label={F4_ACTION_LABEL[action]}
          size="small"
          sx={{
            height: 20,
            borderRadius: 1,
            fontSize: 11,
            fontWeight: 600,
            bgcolor:
              action === 'skip_next_cycle'
                ? '#ff424a'
                : action === 'reduce'
                  ? '#EECD1D'
                  : '#2ea043',
            color: action === 'reduce' ? '#000' : '#fff',
            '& .MuiChip-label': { px: 0.85 },
          }}
        />
      </Box>
      <Stack spacing={0.5}>
        <FieldRow label="Recommended">{`${formatNumber(recommendedWaterBudget, 1)}% (current ${formatNumber(program.currentWaterBudget, 1)}%)`}</FieldRow>
        <FieldRow label="Change">{deltaText}</FieldRow>
      </Stack>
      {proposedUpdate && onApplyRecommendation && (
        <Box sx={{ mt: 0.75 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={disableActions}
            onClick={() => {
              // proposedUpdate is a HINT (id + waterBudget only), not a complete
              // updateProgram payload. Instruct the agent to apply it; the agent
              // re-fetches the full program and calls updateProgram, which runs
              // the real confirm gate and surfaces a ProposedActionCard for the
              // user to confirm.
              onApplyRecommendation(
                `Apply the recommended weather adjustment to program #${proposedUpdate.id}: set waterBudget to ${proposedUpdate.waterBudget}%. Re-fetch the program first, then call updateProgram so I can confirm the change.`,
              )
            }}
          >
            Apply
          </Button>
        </Box>
      )}
      <RationaleSection
        basis={{ lookAhead, forecast, perStation }}
        caveats={caveats}
        basisLabel="Look-ahead and per-station"
      />
    </CardShell>
  )
}

// ─── F5 analyzeMoistureTrend ─────────────────────────────────────────
const F5Card = ({ result }: { result: unknown }) => {
  if (isF5Error(result)) {
    const err = result as F5Error
    const title =
      err.error === 'insufficient_data'
        ? 'Insufficient moisture data'
        : 'Could not load moisture history'
    return (
      <CardShell title="Soil moisture trend" severity="warning">
        <ErrorBanner
          title={title}
          message={
            err.message ||
            (err.error === 'insufficient_data'
              ? 'Not enough moisture samples were collected in the window to detect a trend.'
              : 'The moisture history service returned an error. Please retry later.')
          }
        />
      </CardShell>
    )
  }
  if (!isF5Result(result)) return null
  const { classification, projection, likely_cause, recommendation, metrics, programHistoryWarning } = result
  return (
    <CardShell
      title="Soil moisture trend"
      severity={
        classification === 'stable' || classification === 'recovering'
          ? 'success'
          : classification === 'drying'
            ? 'warning'
            : 'error'
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          mb: 0.5,
        }}
      >
        <ClassificationBadge classification={classification} />
        <Typography sx={{ fontSize: 12, color: 'grey.800' }}>
          {`${metrics.sampleCount} sample${metrics.sampleCount === 1 ? '' : 's'}`}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: 'text.main' }}>
        {recommendation}
      </Typography>
      {likely_cause && (
        <Typography sx={{ fontSize: 11.5, color: 'grey.800', mt: 0.25 }}>
          {F5_CAUSE_LABEL[likely_cause]}
        </Typography>
      )}
      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
        <FieldRow label="Mean">{`${formatNumber(metrics.meanMoisturePercent, 1)}%`}</FieldRow>
        <FieldRow label="Last">{`${formatNumber(metrics.lastMoisturePercent, 1)}%`}</FieldRow>
        <FieldRow label="Projected">{`${formatNumber(projection.projectedMoisturePercent, 1)}% in ${projection.days}d`}</FieldRow>
      </Stack>
      {programHistoryWarning && (
        <Typography sx={{ fontSize: 11.5, color: 'grey.800', mt: 0.5 }}>
          {programHistoryWarning}
        </Typography>
      )}
      <RationaleSection basis={metrics} basisLabel="Metrics" />
    </CardShell>
  )
}

// ─── Top-level dispatcher ────────────────────────────────────────────
interface RecommendationCardProps {
  toolName: string
  result: unknown
  onApplyRecommendation?: (instruction: string) => void
  disableActions?: boolean
}

const RecommendationCard = ({
  toolName,
  result,
  onApplyRecommendation,
  disableActions = false,
}: RecommendationCardProps) => {
  switch (toolName) {
    case RECOMMENDATION_TOOL_F1:
      return <F1Card result={result} />
    case RECOMMENDATION_TOOL_F2:
      return (
        <F2Card
          result={result}
          onApplyRecommendation={onApplyRecommendation}
          disableActions={disableActions}
        />
      )
    case RECOMMENDATION_TOOL_F3:
      return <F3Card result={result} />
    case RECOMMENDATION_TOOL_F4:
      return (
        <F4Card
          result={result}
          onApplyRecommendation={onApplyRecommendation}
          disableActions={disableActions}
        />
      )
    case RECOMMENDATION_TOOL_F5:
      return <F5Card result={result} />
    default:
      return null
  }
}

export default RecommendationCard
